import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/api';

/**
 * Hook for fetching analytics summary data
 * Includes: average gain, top/bottom performers, concentration risk
 * @param {Object} options - Options for filtering
 * @param {boolean} options.excludeRealEstate - Exclude real estate holdings from calculations
 */
export const useAnalyticsSummary = (options = {}) => {
  const { excludeRealEstate = false } = options;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_BASE_URL}/analytics/summary`, {
        params: { excludeRealEstate },
        withCredentials: true
      });

      setData(response.data);
    } catch (err) {
      console.error('Error fetching analytics summary:', err);
      setError(err.response?.data?.error || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [excludeRealEstate]);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh };
};

/**
 * Hook for fetching ETF comparison data
 * @param {number} days - Number of days to compare (default 90)
 */
export const useETFComparison = (days = 90) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_BASE_URL}/analytics/etf-compare`, {
        params: { days },
        withCredentials: true
      });

      setData(response.data);
    } catch (err) {
      console.error('Error fetching ETF comparison:', err);
      setError(err.response?.data?.error || 'Failed to load ETF comparison');
    } finally {
      setLoading(false);
    }
  }, [days]);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh };
};

/**
 * Hook for fetching performance heatmap data
 * @param {number} days - Number of days to show (default 365)
 */
export const usePerformanceHeatmap = (days = 365) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_BASE_URL}/analytics/heatmap`, {
        params: { days },
        withCredentials: true
      });

      setData(response.data);
    } catch (err) {
      console.error('Error fetching heatmap data:', err);
      setError(err.response?.data?.error || 'Failed to load heatmap');
    } finally {
      setLoading(false);
    }
  }, [days]);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refresh };
};

const analyticsHooks = {
  useAnalyticsSummary,
  useETFComparison,
  usePerformanceHeatmap,
};

export default analyticsHooks;
